import { toast } from "sonner";
import { ScanLine, FileBarChart, Download } from "lucide-react";
import api from "../../lib/api";
import { previewPdfResponse } from "../../lib/pdfPreview";
import { Button } from "../../components/ui/button";
import { fmtEur } from "./wmsUtils";

export default function InventorySection({ sessions, onScan }) {
    const downloadPdf = async (sid, ref) => {
        try {
            const res = await api.get(`/inventory-sessions/${sid}/pdf`, { responseType: "blob" });
            previewPdfResponse(res, `Inventur_${(ref || sid).replace(/[\s/]/g, "_")}.pdf`);
        } catch { toast.error("PDF-Download fehlgeschlagen"); }
    };

    const totalValueDiff = sessions.reduce((s, x) => s + Number(x.total_value_diff || 0), 0);
    const totalItems = sessions.reduce((s, x) => s + Number(x.items_count || 0), 0);

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Inventuren gesamt</div>
                    <div className="font-display font-black text-2xl mt-1">{sessions.length}</div>
                </div>
                <div className="bg-white border border-gray-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Artikel-Buchungen kumuliert</div>
                    <div className="font-display font-black text-2xl mt-1">{totalItems}</div>
                </div>
                <div className={`border rounded-sm p-3 ${totalValueDiff < 0 ? "bg-red-50 border-red-200" : totalValueDiff > 0 ? "bg-green-50 border-green-200" : "bg-white border-gray-200"}`}>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Gesamt-Wert-Δ (EK)</div>
                    <div className={`font-display font-black text-2xl mt-1 ${totalValueDiff < 0 ? "text-red-700" : totalValueDiff > 0 ? "text-green-700" : ""}`}>{fmtEur(totalValueDiff)}</div>
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={onScan} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm" data-testid="inv-start-btn">
                    <ScanLine className="w-4 h-4 mr-1.5" /> Neue Inventur (Scanner)
                </Button>
            </div>

            {sessions.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-sm p-12 text-center" data-testid="inventories-empty">
                    <FileBarChart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <div className="font-display font-black text-lg">Noch keine Inventuren erfasst</div>
                    <p className="text-sm text-gray-500 mt-1">Starten Sie Ihre erste Inventur per Barcode-Scanner — der GoBD-Beleg wird automatisch erzeugt.</p>
                </div>
            ) : (
                <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto" data-testid="inventory-list">
                    <table className="w-full text-sm min-w-[800px]">
                        <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600">
                            <tr>
                                <th className="text-left px-3 py-3 font-bold">Datum</th>
                                <th className="text-left px-3 py-3 font-bold">Beleg / Referenz</th>
                                <th className="text-left px-3 py-3 font-bold">Mitarbeiter</th>
                                <th className="text-right px-3 py-3 font-bold">Artikel</th>
                                <th className="text-right px-3 py-3 font-bold">Wert-Δ (EK)</th>
                                <th className="text-right px-3 py-3 font-bold">PDF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sessions.map((s) => {
                                const valDiff = Number(s.total_value_diff || 0);
                                return (
                                    <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`inv-row-${s.id}`}>
                                        <td className="px-3 py-2 text-gray-700 font-mono-num text-[12px]">
                                            {(s.created_at || "").slice(0, 16).replace("T", " ")}
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-bold">{s.reference || "—"}</div>
                                        </td>
                                        <td className="px-3 py-2 text-gray-700">{s.employee || s.created_by_name || <span className="text-gray-300">—</span>}</td>
                                        <td className="px-3 py-2 text-right font-mono-num font-bold">{s.items_count}</td>
                                        <td className={`px-3 py-2 text-right font-mono-num font-bold ${valDiff < 0 ? "text-red-700" : valDiff > 0 ? "text-green-700" : "text-gray-600"}`}>
                                            {fmtEur(valDiff)}
                                        </td>
                                        <td className="px-3 py-2 text-right">
                                            <Button size="sm" variant="outline" onClick={() => downloadPdf(s.id, s.reference)} className="h-7 text-[11px] rounded-sm" data-testid={`inv-pdf-${s.id}`}>
                                                <Download className="w-3 h-3 mr-1" /> PDF
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
